import { memo } from 'react';
import numeral from 'numeral';
import shallow from 'zustand/shallow';
import Icon from '@ant-design/icons';

import SkeletonWrapper from 'global/Securities/components/SkeletonWrapper';
import Chips from 'global/Securities/components/Chips';
import { LightningSvg } from 'features/day-trade/components/Icons';
import useCashInfoStore from 'global/Securities/stores/useCashInfoStore';

import {
  LeverageInfoWrapper,
  InfoRow,
  DividerWrapper,
  SymbolChip,
  RowDivider,
  BuyingPowerWording,
  ChipSkeleton,
  PriceSkeleton,
} from './leverage.styled';

interface LeverageInfoProps {
  multiplier: number;
}

const LeverageInfo = ({ multiplier }: LeverageInfoProps) => {
  const [
    cashInfoError,
    isCashInfoLoading,
    dayTradeBuyingPower,
    dayTradeLeverage,
    dayTradeTradingBalance,
  ] = useCashInfoStore(
    (s) => [
      s.error,
      s.isLoading,
      s.dayTrade,
      s.dayTradeLeverage,
      s.dayTradeTradingBalance,
    ],
    shallow,
  );

  const getBalance = (balance: number) =>
    `Rp${numeral(Math.floor(balance)).format('0,00')}`;

  if (cashInfoError) return null;

  return (
    <LeverageInfoWrapper onClick={(e) => e.stopPropagation()}>
      <InfoRow>
        <span>Trading Balance</span>

        <SkeletonWrapper
          skeleton={<PriceSkeleton active />}
          loading={isCashInfoLoading}
        >
          <span>{getBalance(dayTradeTradingBalance)}</span>
        </SkeletonWrapper>
      </InfoRow>

      <DividerWrapper>
        <RowDivider dashed />

        <SymbolChip>+</SymbolChip>

        <RowDivider dashed />
      </DividerWrapper>

      <InfoRow>
        <span>Leverage</span>

        <SkeletonWrapper
          skeleton={<PriceSkeleton active />}
          loading={isCashInfoLoading}
        >
          <span>{getBalance(dayTradeLeverage)}</span>
        </SkeletonWrapper>
      </InfoRow>

      <DividerWrapper>
        <RowDivider dashed />

        <SymbolChip>=</SymbolChip>

        <RowDivider dashed />
      </DividerWrapper>

      <InfoRow isBold>
        <BuyingPowerWording>
          <span>Buying Power</span>

          <SkeletonWrapper
            skeleton={<ChipSkeleton active />}
            loading={isCashInfoLoading}
          >
            <Chips className="multiplier-chip">
              <Icon component={LightningSvg} />

              {`${multiplier}x`}
            </Chips>
          </SkeletonWrapper>
        </BuyingPowerWording>

        <SkeletonWrapper
          skeleton={<PriceSkeleton active />}
          loading={isCashInfoLoading}
        >
          <span>{getBalance(dayTradeBuyingPower)}</span>
        </SkeletonWrapper>
      </InfoRow>
    </LeverageInfoWrapper>
  );
};

export default memo(LeverageInfo);
